/*
 * r16 (2026-10-03) TikTok groundwork - which network a slot's account is on, as the server says.
 * NecroMancerStack/docs/tiktok/TIKTOK-SUPPORT-DESIGN.md sections 2.1-2.2.
 *
 * Setting 15 "Platform" (javaField socialPlatform) is defined by the SERVER: GET /uiapi/settings/metadata lists it in
 * "Device Info" as a select (INSTAGRAM / TIKTOK) while -Dnecro.server.tiktok is ON, and lib/serverSettings.js adds it to
 * the page like every server-only setting - this bundle invents neither the field nor its options
 * (memory: v3-must-not-invent-what-v1-defines). The server also sends, for each slot, the platform flags in the
 * slot's gates map, and appends each setting's platform gate to its gatedBy, so the page's existing gate code
 * (SlotSettingsPage hiddenByGate) hides Instagram-only settings on a TikTok slot with no code here.
 *
 * What this file adds is the little the page cannot get from data:
 *   - the slot's platform (for the username label and the TikTok note);
 *   - the per-platform labels (V3's local label wins over the server's - fieldMetadataComplete userName);
 *   - the name of the "TikTok Settings" section (KNOWN_SECTIONS / SECTION_ORDER), for the TikTok-only settings
 *     (ids 2001+) the server will define with the TikTok worker modules.
 * A server before r16 sends neither "platform" nor value 15: every slot reads as Instagram and nothing changes.
 */

export const PLATFORM_FIELD = 'socialPlatform';
export const PLATFORM_SETTING_ID = 15;
export const TIKTOK_SECTION = 'TikTok Settings';

export const PLATFORM_NAMES = { INSTAGRAM: 'Instagram', TIKTOK: 'TikTok' };

/**
 * The slot's platform from GET /uiapi/slots/{slot}/settings: its "platform" (r16), else value 15, else INSTAGRAM.
 * Upper case. An unknown stored value comes back as itself (the server holds such a slot).
 */
export function slotPlatform(slotData) {
  if (!slotData) return 'INSTAGRAM';
  let p = slotData.platform;
  if ((p === undefined || p === null || String(p).trim() === '') && slotData.values) {
    p = slotData.values[String(PLATFORM_SETTING_ID)];
  }
  if (p === undefined || p === null || String(p).trim() === '' || String(p).trim().toLowerCase() === 'null') return 'INSTAGRAM';
  return String(p).trim().toUpperCase();
}

export function platformName(platform) {
  return PLATFORM_NAMES[platform] || 'an unknown platform';
}

/*
 * Per-platform labels. null = the label as it is (Instagram keeps today's wording exactly). OTHER = a platform this
 * bundle does not know.
 */
const PLATFORM_LABELS = {
  userName: { INSTAGRAM: null, TIKTOK: 'TikTok Acc Username', OTHER: 'Account Username' },
};

/**
 * The label to show for a field on a slot of this platform. r16 TT-3: `serverLabels` = the slot's "platformLabels" from GET
 * /uiapi/slots/{slot}/settings (javaField -> label; the server sends it for a TikTok slot only - e.g. the comment limits that are
 * TikTok's story-like budget): the SERVER's wording wins on a non-Instagram slot (memory: v3-must-not-invent-what-v1-defines).
 */
export function labelForPlatform(fieldName, label, platform, serverLabels) {
  if (platform !== 'INSTAGRAM' && serverLabels && typeof serverLabels === 'object'
    && typeof serverLabels[fieldName] === 'string' && serverLabels[fieldName].trim()) {
    return serverLabels[fieldName];
  }
  const byPlatform = PLATFORM_LABELS[fieldName];
  if (!byPlatform) return label;
  const key = Object.prototype.hasOwnProperty.call(byPlatform, platform) ? platform : 'OTHER';
  const relabel = byPlatform[key];
  return relabel || label;
}

/*
 * The server's per-module platform flags (service/platform/PlatformModule.java) and what each one covers, for the
 * TikTok note only - the hiding never depends on this list. A flag the server sends that is not here is ignored.
 */
const MODULE_FLAGS = [
  ['platformFollow', 'follows'],
  ['platformLike', 'likes'],
  ['platformStoryLike', 'story likes'],
  ['platformUnfollow', 'unfollows'],
  ['platformComment', 'comments'],
  ['platformWatch', 'For You watching'],
  ['platformWelcomeDm', 'welcome DMs'],
  // r16 TT-23b: TikTok posts - the queued video uploaded (server PlatformModule.POST, flag platformPost: it follows the posting decision's settings
  // 602, 609-611, 633-639 that the repost flag already shows; ready by default since server TT-23b)
  ['platformPost', 'posts'],
  // r16 TT-15: TikTok's own reposts (server PlatformModule.REPOST, flag platformRepost: the reposting settings 602, 609-613, 617-620, 629 / 630,
  // 633-639); the other posting settings stay platformPosting - Instagram's posting pipeline (scheduling, scraping), not used on TikTok
  ['platformRepost', 'reposts'],
  // r16 TT-17: TikTok's outbound DMs (server PlatformModule.OUTBOUND_DM, flag platformOutboundDm: the settings 751 / 752 / 755 / 757; 753 / 754,
  // 756 / 758-761 and 1708 stay hidden on TikTok) - ready by default since server TT-17
  ['platformOutboundDm', 'outbound DMs'],
  ['platformPosting', 'posting (uploading videos)'],
  ['platformBoosting', 'boosting'],
  ['platformMute', 'muting'],
  ['platformLogin', 'auto-login'],
];

/** The TikTok features whose settings the server shows on this slot (its gates map), in a fixed order. */
export function readyTikTokModules(gates) {
  if (!gates) return [];
  return MODULE_FLAGS.filter(([flag]) => gates[flag] === true).map(([, what]) => what);
}

/** A field's metadata with its per-platform label (the same object when nothing changes). */
export function withPlatformLabel(meta, fieldName, platform, serverLabels) {
  if (!meta) return meta;
  const label = labelForPlatform(fieldName, meta.label, platform, serverLabels);
  return label === meta.label ? meta : { ...meta, label };
}
